/**
 * Cards das divergências do passo 7 (02/09/2026 — evolução grande de UX,
 * pedido do dono: "está horrível, precisa melhorar MUITO"). Substitui a lista
 * crua por CARDS COM RASTREABILIDADE:
 *
 *  - cada divergência mostra o COMPARATIVO LADO A LADO "Nosso cálculo"
 *    (sillis/azul) × "Domínio (Contmega)" (laranja) com os valores formatados
 *    por unidade (centavos→R$, minutos→H:MM, p.p.→%) e o Δ em destaque
 *    (verde quando zero); sem par de valores (contagem/cadastral/
 *    consistência) a descrição completa ocupa um bloco próprio — NUNCA
 *    truncada invisivelmente;
 *  - linha de CONTEXTO legível: pessoa (nome + matrícula), código do evento,
 *    campo e o escopo traduzido para pt-BR simples;
 *  - AVALIAÇÃO DA IA INLINE (faixa dentro do card, match por id da análise
 *    mais recente): explicação, badge de ladoProvavel, evidências e as ações
 *    por tipo — correcao_v1 → "Preparar correção" (dry-run do import já
 *    existente), avisar_contmega/escalar_agente → texto pronto com copiar;
 *  - CIÊNCIA SUGERIDA PELA IA: cards com autoJustificativa.pode mostram a
 *    justificativa sugerida + checkbox; a barra "Dar ciência às N
 *    selecionadas (sugestão da IA)" agrupa por texto de justificativa e chama
 *    justificar-lote (uma chamada por grupo) — a sugestão é da IA, a ciência
 *    é SEMPRE do dono.
 *
 * Grupos/sub-grupos viram seções leves com título + contagem (Collapse), com
 * as MESMAS funcionalidades de antes: ciência individual ("Dar ciência"),
 * ciência em lote por sub-grupo e justificativa com Spoiler ver mais/menos.
 * Componentes de apresentação — a rede fica na CompetenciaPage.
 */
import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  Collapse,
  CopyButton,
  Divider,
  Group,
  List,
  Paper,
  SimpleGrid,
  Spoiler,
  Text,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconSparkles,
} from "@tabler/icons-react";
import { fmtMoeda, fmtHhMm } from "./format";
import type { DivergenciaItem, GrupoDivergencias, SubGrupoDivergencias } from "./divergencias-grupos";
import {
  LADO_PROVAVEL_COR,
  LADO_PROVAVEL_ROTULO,
  montarArquivoCorrecoesIa,
  type DivergenciaAnaliseIa,
  type ResultadoAnaliseIa,
} from "./ia-tipos";
import type { ArquivoCorrecoes } from "./correcoes-import";

// ---------------------------------------------------------------------------
// Rótulos e formatação (compartilhados com a página)
// ---------------------------------------------------------------------------

export const ROTULO_STATUS_DIV: Record<string, string> = {
  aberta: "Aberta",
  justificada: "Justificada",
  autojustificada: "Auto-justificada",
  resolvida: "Resolvida",
};

export const COR_STATUS_DIV: Record<string, string> = {
  aberta: "red",
  justificada: "sillis",
  autojustificada: "teal",
  resolvida: "gray",
};

/** Valor legível por unidade: centavos → R$, minutos → H:MM, p.p. → %. */
export function fmtValorDivergencia(v: number | null | undefined, unidade: string | null | undefined): string {
  if (v === null || v === undefined) return "—";
  if (unidade === "minutos") return fmtHhMm(v);
  if (unidade === "p.p.") return `${v.toLocaleString("pt-BR")}%`;
  return `R$ ${fmtMoeda(v)}`;
}

const ROTULO_ESCOPO: Record<string, string> = {
  evento: "Evento da folha",
  rubrica: "Rubrica agregada",
  total_pessoa: "Total da pessoa",
  agregado: "Total da empresa",
  cadastral: "Cadastro",
  contagem: "Contagem de pessoas",
  consistencia_txt: "Consistência do arquivo da Contmega",
  retencao_credito: "Retenção de INSS s/ NF",
  saldo_darf: "Saldo DARF",
};

// ---------------------------------------------------------------------------
// Contexto derivado do alvo + descrição (pessoa, evento, campo, escopo)
// ---------------------------------------------------------------------------

export interface ContextoDivergencia {
  escopo: string;
  escopoRotulo: string;
  matricula: string | null;
  pessoaNome: string | null;
  codigoEvento: string | null;
  campo: string | null;
}

/** Extrai o contexto legível da divergência (tolerante — nunca quebra). */
export function contextoDaDivergencia(d: DivergenciaItem): ContextoDivergencia {
  const [escopo, pessoa, terceiro, campo] = d.alvo.split("|");
  const esc = escopo ?? "";
  let matricula: string | null = pessoa || null;
  let pessoaNome: string | null = null;
  // cadastral: "CADASTRAL departamento de 42 (CAROLINA SILVA OLIVEIRA): …"
  let m = d.descricao.match(/de (\d+) \(([^)]+)\)/);
  if (m) {
    matricula = m[1] ?? matricula;
    pessoaNome = (m[2] ?? "").trim() || null;
  } else {
    // evento/rubrica/total: "42 CAROLINA SILVA OLIVEIRA evento 291 (…)" —
    // o nome vem entre a matrícula e a palavra-chave
    m = d.descricao.match(/^(\d+)\s+([A-ZÀ-Ý][A-ZÀ-Ý' ]*?)\s+(?:evento|rubrica|total)\b/i);
    if (m) {
      matricula = m[1] ?? matricula;
      pessoaNome = (m[2] ?? "").trim() || null;
    }
  }
  const codigoEvento = esc === "evento" || esc === "rubrica" ? terceiro || null : null;
  const campoDiv =
    esc === "cadastral" ? terceiro || null : esc === "total_pessoa" || esc === "agregado" ? campo || null : null;
  return {
    escopo: esc,
    escopoRotulo: ROTULO_ESCOPO[esc] ?? esc,
    matricula,
    pessoaNome,
    codigoEvento,
    campo: campoDiv,
  };
}

/** Título legível do card: pessoa (nome × matrícula) + evento/campo. */
export function tituloDaDivergencia(d: DivergenciaItem): string {
  const ctx = contextoDaDivergencia(d);
  const quem = ctx.pessoaNome
    ? `${ctx.pessoaNome}${ctx.matricula ? ` (matr. ${ctx.matricula})` : ""}`
    : ctx.matricula
      ? `matr. ${ctx.matricula}`
      : null;
  let oQue: string;
  const mEvento = d.descricao.match(/evento (\d+) \(([^)]+)\)/);
  if (ctx.codigoEvento) {
    oQue = mEvento ? `evento ${mEvento[1]} — ${mEvento[2]!.trim()}` : `evento ${ctx.codigoEvento}`;
  } else if (ctx.campo) {
    oQue = ctx.escopo === "cadastral" ? `campo "${ctx.campo}"` : ctx.campo;
  } else {
    oQue = ctx.escopoRotulo;
  }
  return quem ? `${quem} · ${oQue}` : oQue;
}

// ---------------------------------------------------------------------------
// Faixa "Avaliação da IA" (dentro do card da divergência e no histórico)
// ---------------------------------------------------------------------------

export function FaixaAvaliacaoIa({
  item,
  onPrepararCorrecao,
}: {
  item: DivergenciaAnaliseIa;
  onPrepararCorrecao?: (arquivo: ArquivoCorrecoes, nomeArquivo: string) => void;
}) {
  const arquivo = montarArquivoCorrecoesIa(item);
  return (
    <Paper withBorder p="xs" mt={6} bg="violet.0" data-testid="faixa-avaliacao-ia">
      <Group gap="xs" mb={4} wrap="wrap">
        <IconSparkles size={14} color="var(--mantine-color-violet-7)" />
        <Text size="xs" fw={700} c="violet.8">Avaliação da IA</Text>
        <Badge data-lado={item.ladoProvavel} color={LADO_PROVAVEL_COR[item.ladoProvavel]} variant="light" size="sm">
          {LADO_PROVAVEL_ROTULO[item.ladoProvavel]}
        </Badge>
      </Group>
      {item.explicacao && (
        <Text size="sm" mb={4} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{item.explicacao}</Text>
      )}
      {item.sugestao && (
        <Paper withBorder p="xs" mb={4} bg="sillis.0" data-testid="sugestao-ia">
          <Text size="xs" fw={700} c="sillis.9" mb={2}>O que fazer (sugestão da IA):</Text>
          <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{item.sugestao}</Text>
        </Paper>
      )}
      {item.evidencias.length > 0 && (
        <List size="xs" spacing={2} withPadding mb={4}>
          {item.evidencias.map((ev, i) => (
            <List.Item key={i}>{ev}</List.Item>
          ))}
        </List>
      )}
      {item.acao.tipo === "correcao_v1" && arquivo && onPrepararCorrecao && (
        <>
          <Button
            size="xs"
            variant="light"
            color="sillis"
            mt={4}
            onClick={() => onPrepararCorrecao(arquivo, `sugestao-ia-${item.id}.json`)}
          >
            Preparar correção
          </Button>
          <Text size="xs" c="dimmed" mt={4}>
            Abre a prévia normal do import de correções — a IA propõe, quem decide aplicar é você.
          </Text>
        </>
      )}
      {item.acao.tipo === "avisar_contmega" && item.acao.textoContmega && (
        <Paper withBorder p="xs" mt={6} bg="orange.0">
          <Group justify="space-between" mb={4}>
            <Text size="xs" fw={700}>Mensagem pronta para enviar à Contmega:</Text>
            <CopyButton value={item.acao.textoContmega}>
              {({ copied, copy }) => (
                <Button size="xs" variant="subtle" color={copied ? "green" : "orange"} leftSection={<IconCopy size={14} />} onClick={copy}>
                  {copied ? "Copiado" : "Copiar"}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{item.acao.textoContmega}</Text>
        </Paper>
      )}
      {item.acao.tipo === "escalar_agente" && item.acao.resumoEscalacao && (
        <Alert color="violet" variant="light" mt={6} title="Escalar para o agente de desenvolvimento">
          <Text size="sm" mb={6} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{item.acao.resumoEscalacao}</Text>
          <Group gap="xs" align="center">
            <CopyButton value={item.acao.resumoEscalacao}>
              {({ copied, copy }) => (
                <Button size="xs" variant="subtle" color={copied ? "green" : "violet"} leftSection={<IconCopy size={14} />} onClick={copy}>
                  {copied ? "Copiado" : "Copiar"}
                </Button>
              )}
            </CopyButton>
            <Text size="xs" c="dimmed">Copie este resumo e traga para o agente de desenvolvimento do sistema.</Text>
          </Group>
        </Alert>
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Card de UMA divergência
// ---------------------------------------------------------------------------

export interface CardDivergenciaProps {
  d: DivergenciaItem;
  /** item da análise de IA mais recente com o MESMO id (undefined = sem faixa). */
  analiseIa?: DivergenciaAnaliseIa;
  /** seleção da ciência sugerida pela IA (só usado quando autoJustificativa.pode). */
  selecionadaIa?: boolean;
  onToggleSelecaoIa?: (id: string) => void;
  onDarCiencia: (d: DivergenciaItem) => void;
  onPrepararCorrecao?: (arquivo: ArquivoCorrecoes, nomeArquivo: string) => void;
}

export function CardDivergencia({ d, analiseIa, selecionadaIa, onToggleSelecaoIa, onDarCiencia, onPrepararCorrecao }: CardDivergenciaProps) {
  const ctx = contextoDaDivergencia(d);
  const temParDeValores = d.calculado !== null && d.calculado !== undefined;
  const deltaZero = d.delta === 0;
  return (
    <Card withBorder padding="sm" mb="xs" radius="md" data-testid="card-divergencia" data-divergencia-id={d.id}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb={6}>
        <div style={{ minWidth: 0 }}>
          <Text size="sm" fw={700} style={{ wordBreak: "break-word" }}>{tituloDaDivergencia(d)}</Text>
          <Group gap={6} mt={4} wrap="wrap">
            <Badge size="sm" color={d.severidade === "bloqueante" ? "red" : d.severidade === "alerta" ? "yellow" : "gray"}>
              {d.severidade}
            </Badge>
            <Badge size="sm" variant={d.status === "aberta" ? "filled" : "light"} color={COR_STATUS_DIV[d.status] ?? "gray"}>
              {ROTULO_STATUS_DIV[d.status] ?? d.status}
            </Badge>
            <Text size="xs" c="dimmed" ff="monospace">nível {d.nivel}</Text>
          </Group>
        </div>
        {d.status === "aberta" && (
          <Button
            size="xs"
            variant="default"
            aria-label="Dar ciência da divergência"
            onClick={() => onDarCiencia(d)}
          >
            Dar ciência
          </Button>
        )}
      </Group>

      {temParDeValores ? (
        <>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" mb={4}>
            <Paper withBorder p="xs" bg="sillis.0" data-testid="valor-nosso">
              <Text size="xs" fw={700} c="sillis.8" tt="uppercase" mb={2}>Nosso cálculo</Text>
              <Text size="lg" fw={700} ff="monospace">{fmtValorDivergencia(d.calculado, d.unidade)}</Text>
            </Paper>
            <Paper withBorder p="xs" bg="orange.0" data-testid="valor-dominio">
              <Text size="xs" fw={700} c="orange.8" tt="uppercase" mb={2}>Domínio (Contmega)</Text>
              <Text size="lg" fw={700} ff="monospace">{fmtValorDivergencia(d.esperado, d.unidade)}</Text>
            </Paper>
          </SimpleGrid>
          {d.delta !== null && d.delta !== undefined && (
            <Badge
              size="lg"
              variant="light"
              color={deltaZero ? "green" : "red"}
              mb={4}
              title={deltaZero ? "Os dois lados fecham no mesmo valor" : "Diferença entre o nosso cálculo e o da Contmega"}
            >
              Δ {d.delta >= 0 ? "+" : ""}{fmtValorDivergencia(d.delta, d.unidade)}{deltaZero ? " — sem diferença" : ""}
            </Badge>
          )}
        </>
      ) : (
        // sem par de valores (contagem/cadastral/consistência): descrição em
        // bloco próprio, SEMPRE completa (sem corte invisível)
        <Paper withBorder p="xs" mb={4} bg="gray.0">
          <Text size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{d.descricao}</Text>
        </Paper>
      )}

      {/* contexto: pessoa, evento, campo e escopo em pt-BR simples */}
      <Group gap={6} mb={4} wrap="wrap">
        <Badge variant="outline" color="gray" size="sm">{ctx.escopoRotulo}</Badge>
        {ctx.pessoaNome && <Badge variant="outline" color="sillis" size="sm">{ctx.pessoaNome}{ctx.matricula ? ` · matr. ${ctx.matricula}` : ""}</Badge>}
        {!ctx.pessoaNome && ctx.matricula && <Badge variant="outline" color="sillis" size="sm">matr. {ctx.matricula}</Badge>}
        {ctx.codigoEvento && <Badge variant="outline" color="gray" size="sm">evento {ctx.codigoEvento}</Badge>}
        {ctx.campo && <Badge variant="outline" color="gray" size="sm">campo {ctx.campo}</Badge>}
      </Group>

      {/* com o comparativo em destaque, a descrição completa vira apoio */}
      {temParDeValores && (
        <Text size="xs" c="dimmed" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{d.descricao}</Text>
      )}

      {d.justificativa && (
        <Spoiler maxHeight={34} showLabel="ver mais" hideLabel="ver menos">
          <Text size="xs" c={d.status === "autojustificada" ? "teal.8" : "sillis.7"} mt={2} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            ✓ {d.justificativa}{d.referenciaEstudo ? ` (${d.referenciaEstudo})` : ""}
          </Text>
        </Spoiler>
      )}

      {/* avaliação da IA inline (match por id — só quando a análise cobriu esta divergência) */}
      {analiseIa && <FaixaAvaliacaoIa item={analiseIa} onPrepararCorrecao={onPrepararCorrecao} />}

      {/* ciência SUGERIDA pela IA — a aplicação é um clique do dono */}
      {analiseIa?.autoJustificativa?.pode && d.status === "aberta" && (
        <Paper withBorder p="xs" mt={6} bg="teal.0" data-testid="ciencia-sugerida-ia">
          <Text size="xs" fw={700} c="teal.8" mb={2}>Ciência sugerida pela IA — a decisão é sua</Text>
          {analiseIa.autoJustificativa.justificativa && (
            <Text size="xs" mb={4} style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {analiseIa.autoJustificativa.justificativa}
            </Text>
          )}
          <Checkbox
            size="xs"
            label="Selecionar para dar ciência com a justificativa da IA"
            checked={selecionadaIa ?? false}
            onChange={() => onToggleSelecaoIa?.(d.id)}
          />
        </Paper>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Seção completa: grupos (Collapse leve) × sub-grupos × cards + barra de
// ciência em lote das sugestões da IA
// ---------------------------------------------------------------------------

export interface LoteCienciaIa {
  ids: string[];
  justificativa: string;
}

export interface SecaoDivergenciasProps {
  grupos: GrupoDivergencias[];
  /** análise de IA mais recente JÁ normalizada (null = nenhuma carregada). */
  analiseIa: ResultadoAnaliseIa | null;
  selecionadasIa: ReadonlySet<string>;
  onToggleSelecaoIa: (id: string) => void;
  /** aplica a ciência das selecionadas: UMA chamada justificar-lote por grupo
   *  de justificativa igual (a sugestão é da IA; o clique é do dono). */
  onCienciaLoteIa: (lotes: LoteCienciaIa[]) => void;
  onDarCiencia: (d: DivergenciaItem) => void;
  onLote: (sg: SubGrupoDivergencias) => void;
  onPrepararCorrecao?: (arquivo: ArquivoCorrecoes, nomeArquivo: string) => void;
}

function GrupoSecao({ grupo, children }: { grupo: GrupoDivergencias; children: React.ReactNode }) {
  // mesma regra de antes: grupos com bloqueantes abertas nascem abertos
  const [aberto, setAberto] = useState(grupo.bloqueantesAbertas > 0);
  return (
    <Paper withBorder radius="md" mb="sm" data-testid="grupo-divergencias">
      <Button
        fullWidth
        variant="subtle"
        color="gray"
        justify="space-between"
        rightSection={aberto ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
        onClick={() => setAberto((v) => !v)}
        styles={{ inner: { justifyContent: "space-between" }, label: { width: "100%" } }}
        h="auto"
        py="xs"
        px="sm"
      >
        <Group gap="sm" wrap="wrap">
          <Text size="sm" fw={700} ff="monospace">{grupo.classe}</Text>
          <Badge variant="light" color="gray">{grupo.itens.length}</Badge>
          {grupo.abertas > 0 ? (
            <Badge color={grupo.piorSeveridade === "bloqueante" ? "red" : grupo.piorSeveridade === "alerta" ? "yellow" : "gray"}>
              {grupo.abertas} aberta(s)
            </Badge>
          ) : (
            <Badge variant="light" color="sillis">todas tratadas</Badge>
          )}
        </Group>
      </Button>
      <Collapse expanded={aberto}>
        <div style={{ padding: "0 var(--mantine-spacing-sm) var(--mantine-spacing-sm)" }}>{children}</div>
      </Collapse>
    </Paper>
  );
}

export function SecaoDivergencias({
  grupos,
  analiseIa,
  selecionadasIa,
  onToggleSelecaoIa,
  onCienciaLoteIa,
  onDarCiencia,
  onLote,
  onPrepararCorrecao,
}: SecaoDivergenciasProps) {
  const analisePorId = new Map<string, DivergenciaAnaliseIa>((analiseIa?.divergencias ?? []).map((d) => [d.id, d]));
  // lotes da ciência sugerida pela IA: agrupa as selecionadas pela MESMA
  // justificativa (uma chamada justificar-lote por texto)
  const lotesIa: LoteCienciaIa[] = [];
  if (analiseIa) {
    const porJustificativa = new Map<string, string[]>();
    for (const id of selecionadasIa) {
      const item = analisePorId.get(id);
      if (!item?.autoJustificativa?.pode) continue;
      const just = item.autoJustificativa.justificativa ?? `Ciência conforme avaliação da IA (${item.titulo}).`;
      porJustificativa.set(just, [...(porJustificativa.get(just) ?? []), id]);
    }
    for (const [justificativa, ids] of porJustificativa) lotesIa.push({ ids, justificativa });
  }
  const totalSelecionadasIa = lotesIa.reduce((acc, l) => acc + l.ids.length, 0);

  return (
    <div>
      {totalSelecionadasIa > 0 && (
        <Alert color="teal" variant="light" mb="sm" icon={<IconSparkles size={16} />} data-testid="barra-ciencia-ia">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Text size="sm">
              <strong>{totalSelecionadasIa}</strong> divergência(s) selecionada(s) com sugestão de ciência da IA
              {lotesIa.length > 1 ? ` (em ${lotesIa.length} grupos de justificativa)` : ""}. A justificativa é a
              sugerida pela IA — <strong>a ciência é sua</strong> e fica registrada na trilha.
            </Text>
            <Button
              size="xs"
              color="teal"
              onClick={() => onCienciaLoteIa(lotesIa)}
              data-testid="botao-ciencia-lote-ia"
            >
              Dar ciência às {totalSelecionadasIa} selecionadas (sugestão da IA)
            </Button>
          </Group>
        </Alert>
      )}
      {grupos.map((g) => (
        <GrupoSecao key={g.classe} grupo={g}>
          {g.subGrupos.map((sg) => (
            <Card key={sg.chave} withBorder padding="sm" mb="xs" radius="md" data-testid="subgrupo-divergencias">
              <Group justify="space-between" wrap="nowrap" mb={6}>
                <Text size="sm" fw={600}>
                  {sg.rotulo} <Text span size="xs" c="dimmed">({sg.itens.length} ocorrência(s))</Text>
                </Text>
                {sg.abertas > 0 && (
                  <Button size="xs" variant="light" onClick={() => onLote(sg)}>
                    Dar ciência em lote ({sg.abertas})
                  </Button>
                )}
              </Group>
              <Divider mb="xs" />
              {sg.itens.map((d) => (
                <CardDivergencia
                  key={d.id}
                  d={d}
                  analiseIa={analisePorId.get(d.id)}
                  selecionadaIa={selecionadasIa.has(d.id)}
                  onToggleSelecaoIa={onToggleSelecaoIa}
                  onDarCiencia={onDarCiencia}
                  onPrepararCorrecao={onPrepararCorrecao}
                />
              ))}
            </Card>
          ))}
        </GrupoSecao>
      ))}
      {analiseIa && (
        <Text size="xs" c="dimmed" mt={4}>
          <Code>divergência id</Code> ↔ análise da IA: a faixa violeta aparece nos cards que a análise mais recente
          cobriu; as demais seguem sem avaliação.
        </Text>
      )}
    </div>
  );
}
